import { runFullControlCode as runPyodide } from '@/lib/services/pyodideService';
import { isWindows, joinPaths } from '@/lib/services/path-utils';

// Check if running in Tauri
export const isTauri = () => {
  return typeof window !== 'undefined' && (
    '__TAURI__' in window ||
    '__TAURI_INTERNALS__' in window
  );
};

export interface ExecutionResult {
  success: boolean;
  result: {
    steps?: any[];
    plot?: {
      data: any[];
      layout: any;
    };
    gcode?: string | { gcode: string };
    stdout?: string;
    fcLoaded?: boolean;
    stepsCount?: number;
  } | null;
  error: string | null;
}

const PACKAGE_MAPPINGS: Record<string, string> = {
  'PIL': 'Pillow',
  'sklearn': 'scikit-learn',
  'cv2': 'opencv-python',
  'yaml': 'pyyaml',
  'bs4': 'beautifulsoup4',
  'dotenv': 'python-dotenv',
  'dateutil': 'python-dateutil',
  'serial': 'pyserial',
  'usb': 'pyusb',
  'skimage': 'scikit-image'
};

function resolvePackageName(moduleName: string): string {
  return PACKAGE_MAPPINGS[moduleName] || moduleName;
}

export async function runCode(code: string, siteVariant: 'py' | 'js', files: Record<string, ArrayBuffer> = {}, sketchId?: string): Promise<ExecutionResult> {
  // If JS variant, always use the JS runtime (web worker)
  if (siteVariant === 'js') {
    const { runFullControlJS } = await import('@/lib/services/jsRuntimeService');
    const resultJson = await runFullControlJS(code, files);
    return JSON.parse(resultJson);
  }

  // If Python variant
  if (isTauri()) {
    try {
      const { Command } = await import('@tauri-apps/plugin-shell');
      const { resolveResource } = await import('@tauri-apps/api/path');
      const { desktopService } = await import('@/lib/desktop-service');

      const scriptPath = await resolveResource('python/py2g_sidecar.py');

      // Get python environment info
      const pythonInfo = await desktopService.getPythonInfo();
      const sitePackages = await desktopService.getSitePackagesPath();
      const tempPath = await desktopService.getTempPath();

      // If sketchId is provided, ensure venv exists and use it
      let venvPath = null;
      if (sketchId) {
        try {
          venvPath = await desktopService.ensureVenv(sketchId);
        } catch (e) {
          console.warn('Failed to ensure venv, falling back to global site-packages', e);
        }
      }

      const executePython = async (): Promise<ExecutionResult> => {
        let command;
        if (pythonInfo.type === 'uv') {
          const args = ['run', 'python', scriptPath];
          const env: Record<string, string> = {
            PYTHONPATH: sitePackages,
            PY2G_SITE_PACKAGES: sitePackages
          };

          if (venvPath) {
            env['VIRTUAL_ENV'] = venvPath;
          }

          const options = {
            cwd: tempPath,
            env: env
          };

          if (pythonInfo.command[0].startsWith('sidecar:')) {
            const sidecarName = pythonInfo.command[0].split(':')[1];
            command = Command.sidecar(sidecarName, args, options);
          } else {
            command = Command.create('uv', args, options);
          }
        } else {
          // Fallback to system python
          let pythonCmd = 'python';
          if (venvPath) {
            const isWin = isWindows();
            pythonCmd = joinPaths(venvPath, isWin ? 'Scripts' : 'bin', 'python');
          }

          command = Command.create(pythonCmd, [scriptPath], {
            cwd: tempPath,
            env: {
              PYTHONPATH: sitePackages,
              PY2G_SITE_PACKAGES: sitePackages
            }
          });
        }

        const child = await command.spawn();

        return new Promise(async (resolve) => {
          let outputBuffer = '';
          let errorBuffer = '';

          command.stdout.on('data', (line) => {
            outputBuffer += line;
            // Try to parse JSON if it looks like a complete object
            if (outputBuffer.trim().endsWith('}')) {
              try {
                const result = JSON.parse(outputBuffer);
                resolve(result);
                child.kill(); // Kill after execution for this PoC
              } catch {
                // Continue buffering
              }
            }
          });

          command.stderr.on('data', (line) => {
            errorBuffer += line;
            console.error('Python stderr:', line);
          });

          command.on('close', () => {
            if (!outputBuffer && errorBuffer) {
              resolve({
                success: false,
                result: null,
                error: errorBuffer
              });
            } else if (!outputBuffer) {
              resolve({
                success: false,
                result: null,
                error: "Process exited without output"
              });
            }
          });

          // Convert files to base64
          const filesBase64: Record<string, string> = {};
          for (const [filename, buffer] of Object.entries(files)) {
            let binary = '';
            const bytes = new Uint8Array(buffer);
            const len = bytes.byteLength;
            for (let i = 0; i < len; i++) {
              binary += String.fromCharCode(bytes[i]);
            }
            filesBase64[filename] = window.btoa(binary);
          }

          // Send code and files
          const input = JSON.stringify({ code, files: filesBase64 });
          await child.write(input + '\n');
        });
      };

      let result = await executePython();

      // Check for ModuleNotFoundError and try to install
      if (!result.success && result.error && sketchId) {
        const match = result.error.match(/ModuleNotFoundError: No module named '([^']+)'/);
        if (match && match[1]) {
          const moduleName = match[1];
          const packageName = resolvePackageName(moduleName);

          console.log(`Missing module: ${moduleName}, trying to install ${packageName}...`);

          try {
            const installResult = await desktopService.installPackageInVenv(sketchId, packageName);

            if (installResult.success) {
              console.log(`Successfully installed ${packageName}, retrying execution...`);
              result = await executePython();
            } else {
              console.error(`Failed to install ${packageName}: ${installResult.output}`);
              result.error += `\n\nAttempted to install '${packageName}' but failed:\n${installResult.output}`;
            }
          } catch (e) {
            console.error('Error during auto-installation:', e);
          }
        }
      }

      return result;

    } catch (error: any) {
      console.error('Tauri execution error:', error);
      return {
        success: false,
        result: null,
        error: error.message || String(error)
      };
    }
  } else {
    // Fallback to Pyodide in browser
    const resultJson = await runPyodide(code);
    return JSON.parse(resultJson);
  }
}
